import { ReactElement, useMemo, useState, useEffect, useCallback } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import Spinner from '../../../../components/Spinner';
import { toInteger } from 'lodash';
import { useOutletContext } from 'react-router-dom';
import { HiOutlineArrowsPointingOut, HiOutlineXMark } from 'react-icons/hi2';
import { Session } from 'types/Session';
import { SessionAnalysisType } from 'types/SessionAnalysisType';
import { Run, LapMarkers } from 'types/Run';
import { DISC_LETTER_NUMBER } from 'constants/apiConstants';
import SectionHeader from 'components/SectionHeader';
import ChipSelector from 'components/ChipSelector';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FileDownloadCSV,
  FileDownloadXLS,
  FileDownloadXLSReport,
} from '../../../../components/DownloadButtons/DownloadButtons';
import { downloadSessionCSV, downloadSessionXLS, downloadSessionReport } from './downloadFunctions';
import { updateRunsBulk } from '../../../../api/runs';
import { useExtraDisplayPrecision } from '../../../../hooks/useExtraDisplayPrecision';

interface TableRow {
  id: string;
  type: 'config' | 'run' | 'traverse' | 'buffer';
  level: number;
  configLetter?: string;
  runNumber?: number;
  runId?: string;
  traverseCount?: number;
  traverseNumber?: number;
  description?: string;
  metadata?: Record<string, string | number | boolean>;
  data: Record<string, number | number[]>;
  deltas: Record<string, number | number[]> | null;
}

type ViewLevel = 'configs' | 'runs' | 'traverses';

type SessionWithAnalysisType = Session & { analysis_type?: SessionAnalysisType };

const arraysEqual = (a: boolean[], b: boolean[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

const cloneLapMarkers = (markers: LapMarkers | null | undefined): LapMarkers | null => {
  if (!markers) return null;
  try {
    return JSON.parse(JSON.stringify(markers));
  } catch (error) {
    console.warn('Failed to clone lap markers', error);
    return markers;
  }
};

const resolveLapMarkersFromSession = (session: Session | undefined, runId: string): LapMarkers | null => {
  if (!session?.configs) return null;
  for (const config of session.configs) {
    if (!config.runs) continue;
    for (const run of config.runs) {
      if (run.id === runId && run.lap_markers) {
        return cloneLapMarkers(run.lap_markers);
      }
    }
  }
  return null;
};

const getBaseLapMarkers = (
  session: Session | undefined,
  runId: string,
  lapMarkersByRun: Record<string, LapMarkers | null>,
  runLookup: Record<string, Run>,
): LapMarkers | null => {
  // Always get the fresh lap_markers from the run, not from cloned state
  // The cloned state may have null fields if they were null when first cloned
  const run = runLookup[runId];
  if (run?.lap_markers) return cloneLapMarkers(run.lap_markers);

  return resolveLapMarkersFromSession(session, runId);
};

const EXCLUSIONS_CATEGORY = 'Exclusions';

// Reusable component for view level controls
const ViewLevelControls = ({
  viewLevel,
  setViewLevel,
  extraPrecision,
  setExtraPrecision,
  sessionId,
  namePrefix = 'viewLevel',
  traverseLabel = 'Traverses',
  showTraverseSelection = false,
  isTraverseSelectionEnabled = false,
  onTraverseSelectionToggle,
  onUpdateTraverses,
  isUpdateDisabled = false,
  isUpdatingTraverses = false,
  isFullscreen = false,
  onToggleFullscreen,
}: {
  viewLevel: ViewLevel;
  setViewLevel: (level: ViewLevel) => void;
  extraPrecision: number;
  setExtraPrecision: (value: number) => void;
  sessionId: string;
  namePrefix?: string;
  traverseLabel?: string;
  showTraverseSelection?: boolean;
  isTraverseSelectionEnabled?: boolean;
  onTraverseSelectionToggle?: (checked: boolean) => void;
  onUpdateTraverses?: () => void;
  isUpdateDisabled?: boolean;
  isUpdatingTraverses?: boolean;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}) => (
  <div className="cr-flex cr-w-full cr-items-center cr-justify-between">
    <div className="cr-flex cr-items-center cr-space-x-6">
      <label className="cr-flex cr-items-center">
        <input
          type="radio"
          name={namePrefix}
          value="configs"
          checked={viewLevel === 'configs'}
          onChange={(e) => setViewLevel(e.target.value as ViewLevel)}
          className="cr-mr-2"
        />
        <span className="cr-text-sm cr-font-medium cr-text-[#03365D]">Configs</span>
      </label>
      <label className="cr-flex cr-items-center">
        <input
          type="radio"
          name={namePrefix}
          value="runs"
          checked={viewLevel === 'runs'}
          onChange={(e) => setViewLevel(e.target.value as ViewLevel)}
          className="cr-mr-2"
        />
        <span className="cr-text-sm cr-font-medium cr-text-[#03365D]">Runs</span>
      </label>
      <label className="cr-flex cr-items-center">
        <input
          type="radio"
          name={namePrefix}
          value="traverses"
          checked={viewLevel === 'traverses'}
          onChange={(e) => setViewLevel(e.target.value as ViewLevel)}
          className="cr-mr-2"
        />
        <span className="cr-text-sm cr-font-medium cr-text-[#03365D]">{traverseLabel}</span>
      </label>

      {showTraverseSelection && (
        <div className="cr-flex cr-items-center cr-space-x-2">
          <label className="cr-flex cr-items-center">
            <input
              type="checkbox"
              checked={isTraverseSelectionEnabled}
              onChange={(e) => onTraverseSelectionToggle?.(e.target.checked)}
              className="cr-mr-2"
            />
            <span className="cr-text-sm cr-font-medium cr-text-[#03365D]">{`Select ${traverseLabel}`}</span>
          </label>
          <button
            type="button"
            onClick={() => onUpdateTraverses?.()}
            disabled={!isTraverseSelectionEnabled || isUpdateDisabled || isUpdatingTraverses}
            className="cr-rounded-md cr-border cr-border-[#03365D] cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-sm cr-font-semibold hover:cr-bg-cirrusBlue-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-80"
            style={{ color: '#03365D' }}
          >
            {isUpdatingTraverses ? 'Updating...' : 'Update'}
          </button>
        </div>
      )}

      {/* Spacer */}
      <div className="cr-flex cr-w-16 cr-justify-center">
        <div className="cr-h-6 cr-border-l cr-border-gray-300"></div>
      </div>

      {/* Extra Precision Number Input */}
      <div className="cr-flex cr-items-center cr-rounded-md cr-border cr-border-solid cr-border-gray-400 cr-bg-white cr-px-3 cr-py-1">
        <span className="cr-mr-3 cr-text-sm cr-font-medium cr-text-[#03365D]">Extra Display Precision</span>
        <div className="cr-flex cr-items-center">
          <button
            type="button"
            onClick={() => setExtraPrecision(Math.max(0, extraPrecision - 1))}
            disabled={extraPrecision === 0}
            className="cr-px-2 cr-py-1 cr-text-base cr-font-bold cr-text-[#03365D] cr-bg-gray-100 cr-rounded cr-cursor-pointer hover:cr-bg-cirrusBlue-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-50"
          >
            −
          </button>
          <input
            type="number"
            value={extraPrecision}
            onChange={(e) => {
              const val = parseInt(e.target.value) || 0;
              setExtraPrecision(Math.min(5, Math.max(0, val)));
            }}
            min="0"
            max="5"
            className="cr-w-12 cr-border-0 cr-text-center cr-text-sm cr-outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:cr-appearance-none [&::-webkit-inner-spin-button]:cr-appearance-none"
          />
          <button
            type="button"
            onClick={() => setExtraPrecision(Math.min(5, extraPrecision + 1))}
            disabled={extraPrecision === 5}
            className="cr-px-2 cr-py-1 cr-text-base cr-font-bold cr-text-[#03365D] cr-bg-gray-100 cr-rounded cr-cursor-pointer hover:cr-bg-cirrusBlue-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-50"
          >
            +
          </button>
        </div>
      </div>
    </div>

    {/* Download Buttons and Fullscreen Toggle */}
    <div className="cr-flex cr-items-center cr-space-x-2">
      <FileDownloadCSV onClick={downloadSessionCSV(sessionId)} />
      <FileDownloadXLS onClick={downloadSessionXLS(sessionId)} />
      <FileDownloadXLSReport onClick={downloadSessionReport(sessionId)} />
      {onToggleFullscreen && (
        <button
          onClick={onToggleFullscreen}
          className="cr-flex cr-h-8 cr-w-8 cr-items-center cr-justify-center cr-rounded-full cr-bg-gray-100 cr-text-[#03365D] cr-transition-colors hover:cr-bg-cirrusBlue-50 active:cr-bg-cirrusBlue-100"
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? (
            <HiOutlineXMark className="cr-h-5 cr-w-5" />
          ) : (
            <HiOutlineArrowsPointingOut className="cr-h-5 cr-w-5" />
          )}
        </button>
      )}
    </div>
  </div>
);

export default function SessionCalculatedData(): ReactElement {
  const { sessionId, session, isSessionFetching, refetchSession } = useOutletContext<{
    sessionId: string;
    session: Session;
    isSessionFetching: boolean;
    refetchSession: () => Promise<void>;
  }>();

  const isSessionLoading = isSessionFetching;
  const sessionError = null; // Not needed for this component

  const [viewLevel, setViewLevel] = useState<ViewLevel>('traverses');
  const [visibleCategories, setVisibleCategories] = useState<Record<string, boolean>>({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [extraPrecision, setExtraPrecision] = useExtraDisplayPrecision();
  const [isTraverseSelectionEnabled, setIsTraverseSelectionEnabled] = useState(false);
  const [inactiveLapSelections, setInactiveLapSelections] = useState<Record<string, boolean[]>>({});
  const [originalInactiveLapSelections, setOriginalInactiveLapSelections] = useState<Record<string, boolean[]>>({});
  const [lapMarkersByRun, setLapMarkersByRun] = useState<Record<string, LapMarkers | null>>({});
  const [dirtyRunIds, setDirtyRunIds] = useState<Set<string>>(new Set());
  const [isUpdatingLapSelections, setIsUpdatingLapSelections] = useState(false);
  const [isWaitingForRefetch, setIsWaitingForRefetch] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const analysisType = (session as SessionWithAnalysisType | undefined)?.analysis_type;
  const traverseLabel =
    analysisType !== undefined &&
    analysisType >= SessionAnalysisType.LOOP &&
    analysisType < SessionAnalysisType.FREERIDE_CALIBRATED
      ? 'Laps'
      : 'Traverses';

  // Cache key for localStorage
  const CACHE_KEY = 'calculatedDataModel_visibleCategories';

  const descriptionColumn = useMemo(() => {
    return session?.columns?.find((column) => column.internal_name === 'description');
  }, [session?.columns]);

  const descriptionCategoryName = descriptionColumn?.category || 'Description';

  const isDescriptionCategoryVisible = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(visibleCategories, descriptionCategoryName)) {
      return visibleCategories[descriptionCategoryName] !== false;
    }
    return true;
  }, [visibleCategories, descriptionCategoryName]);

  const showTraverseSelectionControls = viewLevel === 'traverses';

  const showExclusionsColumn =
    showTraverseSelectionControls && isTraverseSelectionEnabled && isDescriptionCategoryVisible;

  const runLookup = useMemo(() => {
    const lookup: Record<string, Run> = {};
    session?.configs?.forEach((config) => {
      config.runs?.forEach((run) => {
        lookup[run.id] = run;
      });
    });
    return lookup;
  }, [session?.configs]);

  useEffect(() => {
    if (!session?.configs) {
      setInactiveLapSelections({});
      setOriginalInactiveLapSelections({});
      setDirtyRunIds(new Set());
      return;
    }

    const initialSelections: Record<string, boolean[]> = {};
    const markersMap: Record<string, LapMarkers | null> = {};
    session.configs.forEach((config) => {
      config.runs?.forEach((run) => {
        // Get traverse count to determine array size
        const traverseArrays = run.traverse ? Object.values(run.traverse) : [];
        const traverseCount = traverseArrays.length > 0 ? Math.max(...traverseArrays.map((arr) => arr.length)) : 0;

        // Get inactive_laps - it's already a boolean array in the session response
        const inactiveLapsFromResponse = run.lap_markers?.inactive_laps;

        let inactiveBooleans: boolean[];
        if (Array.isArray(inactiveLapsFromResponse) && inactiveLapsFromResponse.length > 0) {
          // If it's already a boolean array, use it directly
          if (typeof inactiveLapsFromResponse[0] === 'boolean') {
            inactiveBooleans = [...inactiveLapsFromResponse];
          } else {
            // If it's an array of indices (legacy format), convert it
            inactiveBooleans = new Array(traverseCount).fill(false);
            inactiveLapsFromResponse.forEach((index) => {
              if (typeof index === 'number' && index >= 0 && index < traverseCount) {
                inactiveBooleans[index] = true;
              }
            });
          }
        } else {
          // No inactive laps
          inactiveBooleans = new Array(traverseCount).fill(false);
        }

        initialSelections[run.id] = inactiveBooleans;
        markersMap[run.id] = cloneLapMarkers(run.lap_markers);
      });
    });

    const originalSelections: Record<string, boolean[]> = {};
    Object.entries(initialSelections).forEach(([runId, laps]) => {
      originalSelections[runId] = [...laps];
    });

    setInactiveLapSelections(initialSelections);
    setOriginalInactiveLapSelections(originalSelections);
    setLapMarkersByRun(markersMap);
    setDirtyRunIds(new Set());
  }, [session?.configs]);

  useEffect(() => {
    if (viewLevel !== 'traverses') {
      setIsTraverseSelectionEnabled(false);
    }
  }, [viewLevel]);

  // Clear waiting state when session finishes loading after refetch
  useEffect(() => {
    if (isWaitingForRefetch && !isSessionLoading) {
      setIsWaitingForRefetch(false);
    }
  }, [isSessionLoading, isWaitingForRefetch]);

  const handleTraverseSelectionToggle = useCallback((checked: boolean) => {
    setUpdateError(null);
    setIsTraverseSelectionEnabled(checked);
  }, []);

  const handleLapToggle = useCallback(
    (runId: string, lapIndex: number) => {
      setUpdateError(null);
      setInactiveLapSelections((prev) => {
        const current = prev[runId] ?? [];
        const nextInactive = [...current];

        // Ensure array is long enough
        while (nextInactive.length <= lapIndex) {
          nextInactive.push(false);
        }

        // Toggle the boolean value at the index
        if (lapIndex >= 0) {
          nextInactive[lapIndex] = !nextInactive[lapIndex];
        }

        const updated = { ...prev, [runId]: nextInactive };
        const original = originalInactiveLapSelections[runId] ?? [];
        setDirtyRunIds((prevDirty) => {
          const dirty = new Set(prevDirty);
          if (arraysEqual(nextInactive, original)) {
            dirty.delete(runId);
          } else {
            dirty.add(runId);
          }
          return dirty;
        });
        return updated;
      });
    },
    [originalInactiveLapSelections],
  );

  const handleUpdateLapSelections = useCallback(async () => {
    if (dirtyRunIds.size === 0) return;
    setIsUpdatingLapSelections(true);
    setUpdateError(null);
    try {
      const payload = Array.from(dirtyRunIds)
        .map((runId) => {
          const run = runLookup[runId];
          if (!run) return null;
          const inactiveBooleans = inactiveLapSelections[runId] ?? [];

          const baseLapMarkers = getBaseLapMarkers(session, runId, lapMarkersByRun, runLookup);

          const payloadMarkers: LapMarkers = baseLapMarkers
            ? {
                edited: baseLapMarkers.edited ?? null,
                idx_end: baseLapMarkers.idx_end ? [...baseLapMarkers.idx_end] : (baseLapMarkers.idx_end ?? null),
                idx_start: baseLapMarkers.idx_start
                  ? [...baseLapMarkers.idx_start]
                  : (baseLapMarkers.idx_start ?? null),
                markers_rel_ts: baseLapMarkers.markers_rel_ts
                  ? [...baseLapMarkers.markers_rel_ts]
                  : (baseLapMarkers.markers_rel_ts ?? null),
                start_ts: baseLapMarkers.start_ts ?? null,
                inactive_laps: inactiveBooleans.some((b) => b) ? inactiveBooleans : null,
              }
            : {
                edited: null,
                idx_end: null,
                idx_start: null,
                markers_rel_ts: null,
                start_ts: null,
                inactive_laps: inactiveBooleans.some((b) => b) ? inactiveBooleans : null,
              };
          return {
            id: runId,
            lap_markers: payloadMarkers,
          };
        })
        .filter((item): item is { id: string; lap_markers: LapMarkers } => item !== null);

      if (payload.length === 0) {
        setDirtyRunIds(new Set());
        return;
      }

      await updateRunsBulk(payload);

      // Refetch the session to get updated calculated data
      setIsWaitingForRefetch(true);
      await refetchSession();

      const refreshedOriginal = { ...originalInactiveLapSelections };
      const updatedMarkers = { ...lapMarkersByRun };
      payload.forEach(({ id, lap_markers }) => {
        const inactiveBooleans = inactiveLapSelections[id] ?? [];
        refreshedOriginal[id] = [...inactiveBooleans];
        updatedMarkers[id] = cloneLapMarkers(lap_markers);
      });
      setOriginalInactiveLapSelections(refreshedOriginal);
      setLapMarkersByRun(updatedMarkers);
      setDirtyRunIds(new Set());
    } catch (error: any) {
      console.error('Failed to update lap selections', error);
      setUpdateError(error?.message ?? 'Failed to update lap selections');
      setIsWaitingForRefetch(false);
    } finally {
      setIsUpdatingLapSelections(false);
    }
  }, [
    dirtyRunIds,
    inactiveLapSelections,
    originalInactiveLapSelections,
    runLookup,
    lapMarkersByRun,
    session,
    refetchSession,
  ]);

  const hasPendingLapUpdates = dirtyRunIds.size > 0;

  // Initialize visible categories when data loads
  useEffect(() => {
    if (session?.columns) {
      const allCategoryNames = session.columns.map((col: any) => col.category || 'General');
      const uniqueCategories = Array.from(new Set(allCategoryNames));

      // Try to load from cache first
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const cachedCategories = JSON.parse(cached) as Record<string, boolean>;

          // Create new object with all current categories, using cached values where available
          const newVisibleCategories: Record<string, boolean> = {};
          uniqueCategories.forEach((category: any) => {
            newVisibleCategories[category] = cachedCategories[category] ?? true; // Default to true for new categories
          });

          setVisibleCategories(newVisibleCategories);
          return;
        }
      } catch (error) {
        console.warn('Failed to load category cache:', error);
      }

      // If no cache or cache failed, default all to true
      const defaultCategories: Record<string, boolean> = {};
      uniqueCategories.forEach((category) => {
        defaultCategories[category] = true;
      });
      setVisibleCategories(defaultCategories);
    }
  }, [session?.columns]);

  // Special formatting function for specific channels
  const specialChannels = (internalName: string, value: any): string | null => {
    switch (internalName) {
      case 'run_ts_tz':
        // Convert ISO timestamp to hh:mm format
        if (typeof value === 'string') {
          try {
            const date = new Date(value);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          } catch {
            return value; // Return original if parsing fails
          }
        }
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
          try {
            const date = new Date(value[0]);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          } catch {
            return String(value[0]); // Return original if parsing fails
          }
        }
        return null;
      default:
        return null;
    }
  };

  // Helper function to create cell rendering logic
  const createCellRenderer = (column: any, isDelta: boolean = false) => {
    const CellRenderer = ({ getValue }: { getValue: () => any }) => {
      const value = getValue();

      // Return empty if no delta data (for delta columns only)
      if (isDelta && (value === null || value === undefined)) return '';

      // Check for special channel formatting first
      const specialFormatted = specialChannels(column.internal_name, value);
      if (specialFormatted !== null) {
        return specialFormatted;
      }

      let displayValue = '';
      let numericValue: number | null = null;

      // If places is -1, treat as string
      if (column.places === -1) {
        if (typeof value === 'string') displayValue = value;
        else if (Array.isArray(value) && value.length > 0) displayValue = String(value[0]);
        else displayValue = String(value || '');
      } else {
        // Otherwise treat as number with formatting
        if (typeof value === 'number') {
          numericValue = value;
          // Add extraPrecision to places if places > -1 and extraPrecision > 0
          const basePlaces = column.places ?? 3;
          const decimalPlaces = column.places > -1 && extraPrecision > 0 ? basePlaces + extraPrecision : basePlaces;
          displayValue = value.toFixed(decimalPlaces);
        } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'number') {
          numericValue = value[0];
          // Add extraPrecision to places if places > -1 and extraPrecision > 0
          const basePlaces = column.places ?? 3;
          const decimalPlaces = column.places > -1 && extraPrecision > 0 ? basePlaces + extraPrecision : basePlaces;
          displayValue = value[0].toFixed(decimalPlaces);
        } else {
          displayValue = '';
        }
      }

      // Apply color styling for numeric deltas (delta columns only)
      if (isDelta && numericValue !== null) {
        const colorClass = numericValue < 0.0 ? 'cr-text-blue-600' : 'cr-text-red';
        return <span className={colorClass}>{displayValue}</span>;
      }

      return displayValue;
    };

    CellRenderer.displayName = `CellRenderer_${column.internal_name}_${isDelta ? 'delta' : 'main'}`;
    return CellRenderer;
  };

  // Transform data into flat table rows based on view level
  const tableData = useMemo((): TableRow[] => {
    if (!session?.configs) return [];
    if (!session?.columns) return [];

    const rows: TableRow[] = [];

    session.configs.forEach((config, configIndex: number) => {
      // Skip configs with letter 'DISC'
      if (config.letter_number === DISC_LETTER_NUMBER) {
        return;
      }

      // Extract metadata (non-array values) from config
      const configMetadata: Record<string, string | number | boolean> = {};
      Object.entries(config).forEach(([key, value]) => {
        if (typeof value !== 'object' && key !== 'average' && key !== 'runs') {
          configMetadata[key] = value;
        }
      });

      // Always add config row
      const configRow: TableRow = {
        id: `config-${config.id}-${configIndex}`,
        type: 'config',
        level: 0,
        configLetter: config.letter,
        description: config.description,
        metadata: configMetadata,
        data: config.average || {},
        deltas: config.deltas || null,
      };
      rows.push(configRow);

      // Add run rows if viewing runs or traverses
      if (viewLevel === 'runs' || viewLevel === 'traverses') {
        config.runs?.forEach((run, runIndex) => {
          // Extract metadata (non-array values) from run
          const runMetadata: Record<string, string | number | boolean> = {};
          Object.entries(run).forEach(([key, value]) => {
            if (typeof value !== 'object' && key !== 'average' && key !== 'traverse'
              && key !== 'description') {
              runMetadata[key] = value;
            }
          });

          const traverseArrays = run.traverse ? Object.values(run.traverse) : [];
          const traverseCount = traverseArrays.length > 0 ? Math.max(...traverseArrays.map((arr) => arr.length)) : 0;

          const runRow: TableRow = {
            id: `run-${config.id}-${run.id}-${runIndex}`,
            type: 'run',
            level: 1,
            runNumber: run.run_number,
            runId: run.id,
            traverseCount,
            description: run.description,
            metadata: runMetadata,
            data: run.average,
            deltas: run.deltas,
          };
          rows.push(runRow);

          // Add traverse rows if viewing traverses
          if (viewLevel === 'traverses' && run.traverse && traverseCount > 0) {
            for (let i = 0; i < traverseCount; i++) {
              const traverseData: Record<string, number> = {};
              Object.keys(run.traverse).forEach((key) => {
                traverseData[key] = run.traverse[key][i];
              });

              const traverseRow: TableRow = {
                id: `traverse-${config.id}-${run.id}-${runIndex}-${i}`,
                type: 'traverse',
                level: 2,
                runId: run.id,
                traverseNumber: i + 1,
                data: traverseData,
                deltas: null,
              };
              rows.push(traverseRow);
            }
          }
        });
      }

      // Add buffer row after each config (except the last one)
      if (configIndex < session.configs.length - 1) {
        const bufferRow: TableRow = {
          id: `buffer-${config.id}-${configIndex}`,
          type: 'buffer',
          level: 0,
          data: {},
          deltas: null,
        };
        rows.push(bufferRow);
      }
    });

    return rows;
  }, [session, viewLevel]);

  // Create dynamic columns based on API response with category support
  const tableColumns = useMemo(() => {
    if (!session?.columns) return [];

    const columnHelper = createColumnHelper<TableRow>();

    // Filter columns based on visible categories
    const visibleColumns = session.columns.filter((column) => {
      const category = column.category || 'General';
      if (Object.prototype.hasOwnProperty.call(visibleCategories, category)) {
        return visibleCategories[category] === true;
      }
      return true;
    });

    // Add dynamic data columns based on API response
    const dataColumns = visibleColumns.flatMap((column) => {
      const columns = [];

      // Main data column
      columns.push(
        columnHelper.accessor((row) => row.metadata?.[column.internal_name] ?? row.data[column.internal_name], {
          id: column.internal_name,
          header: column.display_name,
          size: toInteger(40 * (column.width || 1)),
          cell: createCellRenderer(column, false),
          meta: {
            category: column.category,
          },
        }),
      );

      if (showExclusionsColumn && column.internal_name === 'description') {
        columns.push(
          columnHelper.display({
            id: 'exclusions-divider',
            header: '',
            size: 2,
            cell: () => <div className="cr-h-full cr-border-r cr-border-gray-300" />,
            meta: {
              category: EXCLUSIONS_CATEGORY,
            },
          }),
        );

        columns.push(
          columnHelper.display({
            id: 'exclusions',
            header: '',
            size: 100,
            cell: ({ row }) => {
              const originalRow = row.original;
              if (!originalRow.runId) {
                return null;
              }
              if (originalRow.type !== 'traverse' || originalRow.traverseNumber == null) {
                return null;
              }
              const runId = originalRow.runId;
              const lapIndex = Math.max(0, (originalRow.traverseNumber ?? 1) - 1);
              const inactiveBooleans = inactiveLapSelections[runId] ?? [];
              const isInactive = inactiveBooleans[lapIndex] ?? false;

              const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                handleLapToggle(runId, lapIndex);
              };

              return (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%', paddingRight: '30px' }}>
                  <input
                    key={`checkbox-${runId}-${lapIndex}`}
                    type="checkbox"
                    className="cr-h-4 cr-w-4"
                    checked={isInactive}
                    disabled={!isTraverseSelectionEnabled}
                    onChange={handleCheckboxChange}
                    aria-label={`Exclude ${traverseLabel} ${originalRow.traverseNumber}`}
                  />
                </div>
              );
            },
            meta: {
              category: EXCLUSIONS_CATEGORY,
            },
          }),
        );
      }

      // Add delta column if delta is true
      if (column.delta === true) {
        columns.push(
          columnHelper.accessor((row) => row.deltas?.[column.internal_name], {
            id: `${column.internal_name}_delta`,
            header: 'Δ',
            size: toInteger(80 * (column.width || 1)),
            cell: createCellRenderer(column, true),
            meta: {
              category: column.category,
            },
          }),
        );
      }

      return columns;
    });

    return [...dataColumns];
  }, [
    session?.columns,
    visibleCategories,
    extraPrecision,
    showExclusionsColumn,
    inactiveLapSelections,
    handleLapToggle,
    traverseLabel,
    isTraverseSelectionEnabled,
  ]);

  // Group columns by category for header rendering
  const columnCategories = useMemo(() => {
    if (!session?.columns) return [];

    const categoryOrder: Array<{ name: string; count: number }> = [];
    const categoryIndexMap: Record<string, number> = {};

    // Only include visible categories
    session.columns.forEach((column: any) => {
      const category = column.category || 'General';
      const isVisible = Object.prototype.hasOwnProperty.call(visibleCategories, category)
        ? visibleCategories[category] === true
        : true;
      if (!isVisible) {
        return;
      }

      const increment = column.delta === true ? 2 : 1;
      if (categoryIndexMap[category] === undefined) {
        categoryIndexMap[category] = categoryOrder.length;
        categoryOrder.push({ name: category, count: increment });
      } else {
        categoryOrder[categoryIndexMap[category]].count += increment;
      }
    });

    if (showExclusionsColumn) {
      const descriptionIndex = categoryIndexMap[descriptionCategoryName];
      const insertionIndex = descriptionIndex !== undefined ? descriptionIndex + 1 : categoryOrder.length;
      categoryOrder.splice(insertionIndex, 0, { name: EXCLUSIONS_CATEGORY, count: 2 });
    }

    return categoryOrder;
  }, [session?.columns, visibleCategories, showExclusionsColumn, descriptionCategoryName]);

  // Calculate which column indices should have black borders (category separators)
  const categoryBorderIndices = useMemo(() => {
    if (!columnCategories.length) return new Set<number>();

    const indices = new Set<number>();
    let currentIndex = 0;

    // Add border after each category except the last one
    for (let i = 0; i < columnCategories.length - 1; i++) {
      currentIndex += columnCategories[i].count;
      indices.add(currentIndex - 1); // Add border to the last column of each category
    }

    return indices;
  }, [columnCategories]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: false,
  });

  // Get all available categories
  const allCategories = useMemo(() => {
    if (!session?.columns) return [];
    const categories = new Set(session.columns.map((col) => col.category || 'General'));
    return Array.from(categories);
  }, [session?.columns]);

  const formMethods = useForm({
    defaultValues: session,
  });

  return (
    <FormProvider {...formMethods}>
      <SectionHeader title="Calculated Data Results" isLoading={isSessionLoading} className="!cr-mt-14" inset />
      <div className="cr-border-solidcr-border-gray-200 cr-border-b cr-pb-2">
        {/* Category Toggle Buttons */}
        {allCategories.length > 0 && (
          <ChipSelector
            options={allCategories.map((category) => ({ label: category, field: category }))}
            storageKey={CACHE_KEY}
            onSelectionChange={setVisibleCategories}
            className="cr-mt-4"
          />
        )}

        {/* View Level Radio Buttons */}
        <div className="cr-mt-4">
          <ViewLevelControls
            viewLevel={viewLevel}
            setViewLevel={setViewLevel}
            extraPrecision={extraPrecision}
            setExtraPrecision={setExtraPrecision}
            sessionId={sessionId}
            traverseLabel={traverseLabel}
            showTraverseSelection={showTraverseSelectionControls}
            isTraverseSelectionEnabled={isTraverseSelectionEnabled}
            onTraverseSelectionToggle={handleTraverseSelectionToggle}
            onUpdateTraverses={handleUpdateLapSelections}
            isUpdateDisabled={!hasPendingLapUpdates}
            isUpdatingTraverses={isUpdatingLapSelections}
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(true)}
          />
        </div>
        {showTraverseSelectionControls && updateError && !isUpdatingLapSelections && !isWaitingForRefetch && (
          <div className="cr-mt-2 cr-text-sm cr-text-red-600">{updateError}</div>
        )}
        {showTraverseSelectionControls && (isUpdatingLapSelections || isWaitingForRefetch) && (
          <div className="cr-mt-2 cr-text-sm cr-text-blue-600">Updating lap selections and recalculating data...</div>
        )}
      </div>

      <div className="cr-flex-1">
        {isSessionLoading && (
          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
            <Spinner isFetching={true} />
          </div>
        )}

        {sessionError && (
          <div className="cr-p-4 cr-text-center cr-text-red-600">
            Error loading data: {sessionError instanceof Error ? sessionError.message : 'Unknown error'}
          </div>
        )}

        {!isSessionLoading && !sessionError && (
          <div className="cr-relative">
            <div
              className={`cr-border-cirrus-blue-500 cr-rounded-lg cr-border-2 cr-border-solid
                ${isFullscreen ? 'cr-fixed cr-inset-x-0 cr-bottom-0 cr-top-[88px] cr-z-5000 cr-bg-white' : 'cr-max-h-[calc(100vh-380px)] cr-overflow-x-auto cr-overflow-y-auto'}`}
              style={{ maxWidth: isFullscreen ? '100vw' : '120vw' }}
            >
              {/* Controls section in fullscreen mode */}
              {isFullscreen && (
                <div className="cr-sticky cr-top-0 cr-z-20 cr-border-b cr-border-gray-200 cr-bg-white cr-px-4 cr-pb-2 cr-pt-4">
                  {/* Category Toggle Buttons */}
                  {allCategories.length > 0 && (
                    <ChipSelector
                      options={allCategories.map((category) => ({ label: category, field: category }))}
                      storageKey={CACHE_KEY}
                      onSelectionChange={setVisibleCategories}
                      className=""
                    />
                  )}

                  {/* View Level Radio Buttons */}
                  <div className="cr-mt-4">
                    <ViewLevelControls
                      viewLevel={viewLevel}
                      setViewLevel={setViewLevel}
                      extraPrecision={extraPrecision}
                      setExtraPrecision={setExtraPrecision}
                      sessionId={sessionId}
                      namePrefix="viewLevelFullscreen"
                      traverseLabel={traverseLabel}
                      showTraverseSelection={showTraverseSelectionControls}
                      isTraverseSelectionEnabled={isTraverseSelectionEnabled}
                      onTraverseSelectionToggle={handleTraverseSelectionToggle}
                      onUpdateTraverses={handleUpdateLapSelections}
                      isUpdateDisabled={!hasPendingLapUpdates}
                      isUpdatingTraverses={isUpdatingLapSelections}
                      isFullscreen={isFullscreen}
                      onToggleFullscreen={() => setIsFullscreen(false)}
                    />
                    {showTraverseSelectionControls &&
                      updateError &&
                      !isUpdatingLapSelections &&
                      !isWaitingForRefetch && <div className="cr-mt-2 cr-text-sm cr-text-red-600">{updateError}</div>}
                    {showTraverseSelectionControls && (isUpdatingLapSelections || isWaitingForRefetch) && (
                      <div className="cr-mt-2 cr-text-sm cr-text-blue-600">
                        Updating lap selections and recalculating data...
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div
                className={isFullscreen ? 'cr-overflow-x-auto cr-overflow-y-auto' : 'cr-pb-24'}
                style={{ height: isFullscreen ? 'calc(100% - 120px)' : 'auto' }}
              >
                <table className="cr-border-separate cr-border-spacing-0" style={{ width: 'max-content' }}>
                  <thead className="cr-sticky cr-top-0 cr-z-10 cr-bg-white">
                    {/* Category row */}
                    {columnCategories.length > 0 && (
                      <tr className="">
                        {columnCategories.map((category, index) => (
                          <th
                            key={category.name}
                            colSpan={category.count}
                            className="cr-px-3 cr-py-2 cr-text-center cr-text-sm cr-font-bold cr-uppercase cr-tracking-wider cr-text-[#03365D]"
                            style={{
                              backgroundColor: '#CCDDE5',
                              borderRight: index < columnCategories.length - 1 ? '2px solid #9CA3AF' : undefined,
                            }}
                          >
                            {category.name}
                          </th>
                        ))}
                      </tr>
                    )}
                    {/* Column headers row */}
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} className="cr-border-b cr-border-gray-200">
                        {headerGroup.headers.map((header, index) => (
                          <th
                            key={header.id}
                            className="cr-px-3 cr-py-2 cr-text-left cr-text-xs cr-font-medium cr-uppercase cr-tracking-wider cr-text-[#03365D]"
                            style={{
                              width: `${header.getSize()}px`,
                              borderRight: categoryBorderIndices.has(index) ? '2px solid #9CA3AF' : undefined,
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                    {/* Units row */}
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={`${headerGroup.id}-units`} className="cr-border-b cr-border-gray-200">
                        {headerGroup.headers.map((header, index) => {
                          // Find the corresponding column definition to get units
                          const columnId = header.id.replace('_delta', ''); // Remove delta suffix if present
                          const columnDef = session?.columns?.find((col) => col.internal_name === columnId);
                          const units = columnDef?.units || '';

                          return (
                            <th
                              key={`${header.id}-units`}
                              className="cr-px-3 cr-py-0 cr-text-left cr-text-xs cr-font-normal cr-italic cr-text-[#03365D]"
                              style={{
                                width: `${header.getSize()}px`,
                                borderRight: categoryBorderIndices.has(index) ? '2px solid #9CA3AF' : undefined,
                              }}
                            >
                              {units}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Buffer row after units */}
                    <tr className="cr-bg-white">
                      {table.getFlatHeaders().map((header, index) => (
                        <th
                          key={`${header.id}-buffer`}
                          className="cr-border-cirrus-blue-500 cr-border-b-2 cr-border-solid cr-px-3 cr-py-1"
                          style={{
                            width: `${header.getSize()}px`,
                            borderRight: categoryBorderIndices.has(index) ? '2px solid #9CA3AF' : undefined,
                          }}
                        >
                          &nbsp;
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="cr-divide-y cr-divide-gray-200 cr-bg-white">
                    {table.getRowModel().rows.map((row) => {
                      const bgColor =
                        row.original.type === 'config'
                          ? 'cr-bg-cirrusBlue-100'
                          : row.original.type === 'run'
                            ? 'cr-bg-cirrusBlue-50'
                            : row.original.type === 'traverse'
                              ? 'cr-bg-gray-50'
                              : row.original.type === 'buffer'
                                ? 'cr-bg-white'
                                : 'cr-bg-yellow-50';

                      // For buffer rows, render an empty row with minimal height
                      if (row.original.type === 'buffer') {
                        return (
                          <tr key={row.id} className="cr-bg-white">
                            {row.getVisibleCells().map((cell, index) => (
                              <td
                                key={cell.id}
                                className="cr-border-r cr-border-gray-200 cr-px-3 cr-py-1"
                                style={{
                                  width: `${cell.column.getSize()}px`,
                                  borderRight: categoryBorderIndices.has(index) ? '2px solid #9CA3AF' : undefined,
                                }}
                              >
                                &nbsp;
                              </td>
                            ))}
                          </tr>
                        );
                      }

                      // Determine text styling based on row type
                      const baseTextClasses =
                        row.original.type === 'config'
                          ? 'cr-text-base cr-font-semibold cr-text-[#03365D]'
                          : row.original.type === 'run'
                            ? 'cr-text-sm cr-font-medium cr-text-[#03365D]'
                            : 'cr-text-xs cr-font-normal cr-text-[#03365D]';

                      // Check if this traverse/lap is inactive
                      const isInactive =
                        row.original.type === 'traverse' &&
                        row.original.runId &&
                        row.original.traverseNumber != null &&
                        (inactiveLapSelections[row.original.runId]?.[row.original.traverseNumber - 1] ?? false);

                      const textClasses = isInactive ? `${baseTextClasses} cr-italic cr-opacity-50` : baseTextClasses;

                      return (
                        <tr key={row.id} className={`${bgColor} cr-hover:bg-opacity-75`}>
                          {row.getVisibleCells().map((cell, index) => {
                            const isExclusionsColumn = cell.column.id === 'exclusions';
                            return (
                              <td
                                key={cell.id}
                                className={`cr-font-mono cr-border-r cr-border-gray-200 cr-py-2 cr-align-middle ${isExclusionsColumn ? 'cr-px-0' : 'cr-px-3'} ${textClasses}`}
                                style={{
                                  width: `${cell.column.getSize()}px`,
                                  borderRight: categoryBorderIndices.has(index) ? '2px solid #9CA3AF' : undefined,
                                  textAlign: isExclusionsColumn ? 'center' : undefined,
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {!isSessionLoading && !sessionError && tableData.length === 0 && (
          <div className="cr-p-4 cr-text-center cr-text-gray-600">No calculated data available for this session.</div>
        )}
      </div>
    </FormProvider>
  );
}
